import { addFunctionSerializer } from '@vanilla-extract/css/functionSerializer';

type AttributeValue = boolean | string | number | null;

type MakeAttributeReturnType<TAttributeValue extends AttributeValue> = {
  /**
   * Returns a CSS selector string for the given attribute value.
   *
   * @param {TAttributeValue | TAttributeValue[]} [value] - The attribute value which we should construct a CSS selector string for.
   *                              You may also provide an array of values which will be combined into a single selector using :is().
   *                              If an undefined value is provided, the selector will match any element with the attribute present.
   * @param {string} [scopedSelectorOverride] - An optional override for the selector which the constructed attribute selector should be scoped to.
   *
   * @example
   * // Returns '[data-foo="bar"]'
   * makeAttribute('data-foo').selector('bar');
   * @example
   * // Returns '[data-foo]'
   * makeAttribute('data-foo').selector();
   * @example
   * // Returns ':is([data-foo="bar"], [data-foo="baz"])'
   * makeAttribute('data-foo').selector(['bar', 'baz']);
   * @example
   * // Returns '.my-element[data-foo="bar"]'
   * makeAttribute('data-foo').selector('bar', '.my-element');
   */
  selector: (
    value?: TAttributeValue | TAttributeValue[],
    scopedSelectorOverride?: string,
  ) => string;
  /**
   * Returns an object containing the attribute name and value which can be spread on a JSX element
   * to apply the attribute.
   */
  props: (value?: TAttributeValue) => Record<string, string>;
  /**
   * The name of the attribute.
   */
  attributeName: string;
  /**
   * Returns a string representation of the attribute selector; this should just be
   * the same as calling `selector()` with no arguments.
   */
  toString: () => string;
};

export function makeAttribute<TAttributeValue extends AttributeValue = boolean>(
  name: string,
  scopedSelector = '',
): MakeAttributeReturnType<TAttributeValue> {
  const attr = _makeAttribute<TAttributeValue>(name, scopedSelector);

  addFunctionSerializer(attr, {
    importPath: '@libs/shared-ui-styles/src/utils/makeAttribute',
    importName: '_makeAttribute',
    args: [[name], [scopedSelector]],
  });

  return attr;
}

export function _makeAttribute<
  // Most data attributes are boolean, so we'll default to that if
  // nothing is specified.
  TAttributeValue extends AttributeValue = boolean,
>(name: string, scopedSelector = ''): MakeAttributeReturnType<TAttributeValue> {
  function selector(
    value?: TAttributeValue | TAttributeValue[],
    scopedSelectorOverride = scopedSelector,
  ): string {
    if (value === undefined) {
      return `${scopedSelectorOverride}[${name}]`;
    }

    if (value === null || value === false) {
      return `${scopedSelectorOverride}:not([${name}])`;
    }

    if (Array.isArray(value)) {
      // If an array of values is provided, combine them into a single selector with :is()
      return `${scopedSelectorOverride}:is(${value.map((v) => selector(v, '')).join(', ')})`;
    }

    return `${scopedSelectorOverride}[${name}="${value}"]`;
  }

  const props = (value?: TAttributeValue) => {
    if (value === null || value === false) {
      return {};
    }

    return { [name]: String(value ?? '') };
  };

  // This is weird. There might be a better way to do this, but this at least works.
  // Vanilla Extract's `addFunctionSerializer` is configured to only be able to serialize
  // functions, not objects. However, this is not an actual technical limitation, just
  // the only thing their API officially supports.
  // We want makeAttribute to return an object, but in order to be able to serialize and export
  // it from a Vanilla Extract .css.ts file, we need to smuggle those object properties
  // on the back of a function.
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  function makeAttributeFunction() {}

  Object.defineProperties(makeAttributeFunction, {
    selector: {
      value: selector,
      writable: false,
    },
    props: {
      value: props,
      writable: false,
    },
    attributeName: {
      value: name,
      writable: false,
    },
    toString: {
      value: () => selector(),
      writable: false,
    },
  });

  return makeAttributeFunction as any;
}
